import DrDivider from '@components/DrDivider';
import BottomAppBar from '@components/bottom/BottomAppBar';
import Footer from '@components/bottom/Footer';
import PrimaryButton from '@components/buttons/primary/PrimaryButton';
import Header from '@components/header/Header';
import { LoadingView } from '@components/loading/LoadingView';
import Meta from '@components/meta/Meta';
import MainAvatar from '@components/profile/MainAvatar';
import { User } from '@src/API';
import { useAuthState } from '@src/providers/AuthProvider';
import { RealChatService } from '@src/services/chatService';
import captureException from '@src/services/loggerService';
import { useListAllUsersQuery } from '@src/services/userHooks';
import { truncateText } from '@src/utils/formatText';
import { isNameMatchedQuery } from '@src/utils/search';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

const MetaDescription = `Kết quả tìm kiếm ở Dreamerly: Người dùng, Loại commission, Tranh.`;
type UserSearchResult = User & {
  isButtonLoading: boolean; // If the button for this result is loading
  isOtherResultClicked: boolean; // If another result is clicked, the button for this result should be disabled
};

function SearchPage() {
  const { user: authUser, isAuthenticated } = useAuthState();
  const { isInitialLoading, isError, data } = useListAllUsersQuery({ enabled: true });
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  // Chat service
  const chatService = useMemo(() => new RealChatService(), []);

  const router = useRouter();
  const fullPathUrl = new URL(router.asPath, process.env.NEXT_PUBLIC_DOMAIN || '').href;

  const { q: query } = router.query as { q: string | undefined };
  useEffect(() => {
    if (query && data) {
      const matchedUsers = data
        .filter((user) => {
          return (
            isNameMatchedQuery(user.display_name, query) || isNameMatchedQuery(user.username, query)
          );
        })
        .sort((a, b) => (a?.display_name || '').localeCompare(b?.display_name || ''))
        .map((user) => {
          return {
            ...user,
            isButtonLoading: false,
            isOtherResultClicked: false,
          };
        });
      setUserSearchResults(matchedUsers);
    }
  }, [query, data]);

  const createChannel = async (userSearchResult: UserSearchResult) => {
    try {
      // If there is no user, open the login modal and return
      if (!authUser) {
        return;
      }
      // If isButtonLoading or isOtherResultClicked is true, return
      if (userSearchResult.isButtonLoading || userSearchResult.isOtherResultClicked) {
        return;
      }

      // Set isButtonLoading for this user search result to true, and isOtherResultClicked
      // for all other user search results to true
      const updatedUserSearchResults = userSearchResults.map((result) => {
        if (result.id === userSearchResult.id) {
          return {
            ...result,
            isButtonLoading: true,
          };
        }
        return {
          ...result,
          isOtherResultClicked: true,
        };
      });
      setUserSearchResults(updatedUserSearchResults);

      await chatService.startChat(authUser.id, userSearchResult.id);
      router.push({
        pathname: '/inbox',
      });
    } catch (error) {
      captureException(error);
      // Set isButtonLoading for this user search result to false, and isOtherResultClicked
      // for all other user search results to false
      const updatedUserSearchResults = userSearchResults.map((result) => {
        if (result.id === userSearchResult.id) {
          return {
            ...result,
            isButtonLoading: false,
          };
        }
        return {
          ...result,
          isOtherResultClicked: false,
        };
      });
      setUserSearchResults(updatedUserSearchResults);
    }
  };

  return (
    <div>
      <Meta
        title={`Kết quả tìm kiếm${query ? `: ${truncateText(query, 40)}` : ''} | Dreamerly`}
        url={fullPathUrl}
        description={MetaDescription}
      />
      {!isError && (
        <div className="flex flex-col items-center">
          <Header />
          <div className="max-w-screen-lg w-full flex flex-col my-8 px-4 md:px-6 lg:px-12 pb-24">
            <div className="flex flex-col mb-6 w-full">
              <h1 className="font-semibold text-4xl my-1 break-words">
                Kết quả tìm kiếm
                {query ? `: ${truncateText(query, 40)}` : ''}
              </h1>
            </div>

            {isInitialLoading && <LoadingView />}
            {!isInitialLoading && userSearchResults.length === 0 && (
              <div className="flex flex-col pt-6 items-center">
                <Image width={180} height={120} src="/ic-empty-search.svg" />
                <h2 className="pt-4 font-semibold text-xl">Không tìm thấy kết quả phù hợp</h2>
                <span className="pt-2 pb-4 text-m text-content-t200">
                  Vui lòng thử lại với tên hiển thị hoặc Dreamerly ID khác
                </span>
                <PrimaryButton sizeType="sm" isLink href={`/`}>
                  Trở về trang chủ
                </PrimaryButton>
              </div>
            )}
            {!isInitialLoading &&
              userSearchResults.length > 0 &&
              userSearchResults.map((user, index) => {
                // Check if the user is a leader or follower of authUser
                const isLeader = !!(authUser?.leaders || []).find(
                  (leaderUserFollow) => leaderUserFollow?.leader?.id === user?.id,
                );
                const isFollower = !!(authUser?.followers || []).find(
                  (followerUserFollow) => followerUserFollow?.follower?.id === user?.id,
                );

                let userString = user?.is_artist === 1 ? 'Hoạ sĩ' : 'Khách hàng';
                if (user?.is_artist === 1 && user?.is_selling === 1) {
                  userString += ' · Đang mở commission';
                } else if (user?.is_artist === 1 && user?.is_selling === 0) {
                  userString += ' · Chưa mở commission';
                }
                if (isLeader) {
                  userString += ' · Bạn đang theo dõi';
                }
                if (isFollower) {
                  userString += ' · Đang theo dõi bạn';
                }

                return (
                  <div key={user.id} className="flex flex-col">
                    <div className="flex flex-row py-6 md:py-4 w-full">
                      <MainAvatar
                        sizeType="md"
                        imageSrc={user?.profile_picture_url || ''}
                        linkUrl={`/users/${user?.username || ''}`}
                        displayName={user?.display_name || ''}
                      />
                      <div className="flex flex-col md:flex-row justify-between w-full pl-4">
                        <div className="flex flex-col flex-grow pb-4 md:pb-0">
                          <Link href={`/users/${user?.username || ''}`} passHref>
                            <a className="flex flex-row text-l font-semibold w-fit hover:underline">
                              {truncateText(user?.display_name)}
                            </a>
                          </Link>
                          <span className="pt-1 text-s text-content-t200 flex-wrap">
                            {userString}
                          </span>
                        </div>
                        <div className="flex items-center">
                          {/* If authUser is not defined or if user is the same as authUser, show profile link */}
                          {(!isAuthenticated || user?.id === authUser?.id) && (
                            <PrimaryButton
                              isFullWidth
                              sizeType="sm"
                              isLink
                              href={`/users/${user?.username}`}
                            >
                              Xem trang cá nhân
                            </PrimaryButton>
                          )}
                          {/* If authUser is defined and user is not the same as authUser, show chat button */}
                          {!!isAuthenticated && user?.id !== authUser?.id && (
                            <PrimaryButton
                              isFullWidth
                              sizeType="sm"
                              onClick={() => {
                                createChannel(user);
                              }}
                              isLoading={user.isButtonLoading}
                            >
                              Nhắn tin
                            </PrimaryButton>
                          )}
                        </div>
                      </div>
                    </div>
                    {index < userSearchResults.length - 1 && <DrDivider />}
                  </div>
                );
              })}
          </div>
          <Footer isStickyFooterEnabled />
          <BottomAppBar />
        </div>
      )}
      {!!isError && <div>create error component here</div>}
    </div>
  );
}

export default SearchPage;
